import React from "react";

import { useGetCurrentCompanyApplicationQuery, useGetShortTermDisabilityPlansQuery } from "EmployerApplication/api";
import { UNSUPPORTED_DISABILITY_SIC_CODES } from "EmployerApplication/Components/Constants";
import { CoverageOptionsPageContext } from "EmployerApplication/pages/CoverageOptionsPage";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import formatCurrency from "Utils/formatCurrency";

import { ShortTermDisabilityIcon } from "Cobalt/InsuranceTypeIcon";
import { Popover } from "Cobalt/Popover";
import { Table, TR, SnugTD } from "Cobalt/TableV2";
import { getCart, addPlanToCart } from "Common/CartService";
import DisabilityCostBreakdownModal from "Common/DisabilityCostBreakdownModal";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
// import SBCLinkCell from "ComparePlans/Components/SBCLinkCell";
import { SizeSwitch } from "Common/Responsive";
import SIOverlayTrigger from "Common/SIOverlayTrigger";
import { Body2 } from "Theme/BodyText";
import Button from "Theme/Button";

import {
  ResponsiveAddCard,
  ResponsiveLoadingCard,
  ResponsiveSelectedCard,
  ResponsiveDisabledAddCard,
  SuccessStatus,
  WarningStatus,
  ErrorStatus,
  FlexWrapper,
  StatusMessage,
  PlanDetailsCollapse,
} from "./CompactAncillaryPlanCard";

const AddPlanCard = () => (
  <CoverageOptionsPageContext.Consumer>
    {({ showShortTermDisabilityModal }) => (
      <ResponsiveAddCard
        onClick={() => {
          AnalyticsTracking.trackEvent("cta_clicked", {
            text: "Add Short Term Disability",
            label: "add_short_term_plan_clicked",
            location: "life_disability_new_entry",
          });

          showShortTermDisabilityModal();
        }}
        name="Short Term Disability"
        additionalInfo={<SizeSwitch md={<Body2>Short term disability can cost as little as $10 per employee per month</Body2>} />}
        icon={<ShortTermDisabilityIcon />}
      />
    )}
  </CoverageOptionsPageContext.Consumer>
);

const QUOTED = "quoted";
const QUOTING = "quoting";
const ACTION_REQUIRED = "action_required";

const planStatus = (shortTermPlan) => {
  if (["quoted"].includes(shortTermPlan?.pricing_status)) {
    return QUOTED;
  }
  if (["quote_requested", "complete_census_estimate"].includes(shortTermPlan?.pricing_status)) {
    return QUOTING;
  }
  return ACTION_REQUIRED;
};

const PlanStatus = ({ shortTermPlan }) => {
  const status = planStatus(shortTermPlan);
  if (status === ACTION_REQUIRED) {
    return <ErrorStatus>Action Required</ErrorStatus>;
  }
  if (status === QUOTING) {
    return <WarningStatus>Quoting in Progress</WarningStatus>;
  }
  if (status === QUOTED) {
    return <SuccessStatus>Included</SuccessStatus>;
  }
  return null;
};

const statusMessageText = ({ status }) => {
  switch (status) {
    case ACTION_REQUIRED:
      return "We need additional information in order to provide a quote.";
    case QUOTING:
      return "Quoting is in progress. You should receive an email with quotes for plans selected in 1- 2 business days.";
    default:
      return null;
  }
};

const MonthlyCostTooltip = () => (
  <SIOverlayTrigger
    placement="top"
    overlay={
      <Popover showBackdrop={false} id="more-reasons">
        This price is an estimate. We will display your final price once underwriting is complete.
      </Popover>
    }
  >
    <Icon icon="question-circle" />
  </SIOverlayTrigger>
);

const RemovePlanButton = ({ shortTermPlan }) => {
  const dispatch = useDispatch();
  return (
    <Button
      fullWidth
      intuitProps={{ mx: "auto" }}
      color="destructive"
      onClick={() => {
        dispatch(addPlanToCart({ planType: "short_term_disability", plan: null }));

        AnalyticsTracking.trackEvent("disability_plan_removed", {
          removedPlan: shortTermPlan,
          deprecatedName: `remove-plan/short-term-disability`,
        });
      }}
    >
      Remove Plan
    </Button>
  );
};

const ChangePlanButton = (props) => (
  <CoverageOptionsPageContext.Consumer>
    {({ showShortTermDisabilityModal }) => (
      <Button
        fullWidth
        color="gray"
        intuitProps={{ mx: "auto" }}
        onClick={() => {
          AnalyticsTracking.trackEvent("cta_clicked", {
            text: "Change Short Term Disability",
            location: "short_term_disability_new_entry",
          });

          showShortTermDisabilityModal();
        }}
        {...props}
      >
        Change Selection
      </Button>
    )}
  </CoverageOptionsPageContext.Consumer>
);

const PlanCardContent = ({ shortTermPlan }) => {
  const status = planStatus(shortTermPlan);
  return (
    <>
      <FlexWrapper py={3} my={2} intuitProps={{ py: 2, my: 1 }}>
        <Body2 weight="bold">Total Monthly Cost {status !== QUOTED ? <MonthlyCostTooltip /> : null}</Body2>
        <Body2>{formatCurrency(shortTermPlan.total_cost, 2)}</Body2>
      </FlexWrapper>
      <PlanDetailsCollapse>
        <Div py={3} my={2} intuitProps={{ py: 2, my: 1 }}>
          <Table>
            <tbody>
              {/* <TR>
                 <SnugTD>Plan Document</SnugTD>
                 <SnugTD>
                   <SBCLinkCell plan={shortTermPlan} copy="Download" />
                 </SnugTD>
                </TR> */}
              <TR>
                <SnugTD>Cost Breakdown</SnugTD>
                <SnugTD>
                  <DisabilityCostBreakdownModal plan={shortTermPlan} triggerText="View" />
                </SnugTD>
              </TR>
            </tbody>
          </Table>
        </Div>
        {status !== QUOTED ? (
          <>
            <StatusMessage pt={2} intuitProps={{ pt: 1 }}>
              {statusMessageText({ status })}
            </StatusMessage>
            <Div py={3} my={2} intuitProps={{ py: 2, my: 1 }}>
              <ChangePlanButton />
            </Div>
          </>
        ) : null}
        <RemovePlanButton shortTermPlan={shortTermPlan} />
      </PlanDetailsCollapse>
    </>
  );
};

const MobilePlanCardContent = ({ shortTermPlan }) => {
  const status = planStatus(shortTermPlan);
  return (
    <>
      <Div pt={5} pb={3} mb={2}>
        <Table>
          <tbody>
            {/* <TR>
               <SnugTD>Plan Document</SnugTD>
               <SnugTD>
                 <SBCLinkCell plan={shortTermPlan} copy="Download" />
               </SnugTD>
              </TR> */}
            <TR>
              <SnugTD>Cost Breakdown</SnugTD>
              <SnugTD>
                <DisabilityCostBreakdownModal plan={shortTermPlan} triggerText="View" />
              </SnugTD>
            </TR>
            <TR>
              <SnugTD>Total Monthly cost {status !== QUOTED ? <MonthlyCostTooltip /> : null}</SnugTD>
              <SnugTD>{formatCurrency(shortTermPlan.total_cost, 2)}</SnugTD>
            </TR>
          </tbody>
        </Table>
      </Div>
      {status !== QUOTED ? (
        <>
          <StatusMessage pt={2} intuitProps={{ pt: 1 }}>
            {statusMessageText({ status })}
          </StatusMessage>
          <Div py={3} my={2} intuitProps={{ py: 2, my: 1 }}>
            <ChangePlanButton />
          </Div>
        </>
      ) : null}
      <RemovePlanButton />
    </>
  );
};

/**
 * The SelectedCard component coordinates the differences of content when the selected
 * plan doesn't have a final quote or the employer needs to take some action.
 * @param {shortTermPlan} shortTermPlan
 * @returns
 */
const SelectedCard = ({ shortTermPlan }) => (
  <ResponsiveSelectedCard
    icon={<ShortTermDisabilityIcon />}
    status={<PlanStatus shortTermPlan={shortTermPlan} />}
    name="Short Term Disability"
    additionalInfo={`${shortTermPlan.salary_coverage_percentage}% Salary Replacement`}
  >
    <SizeSwitch xs={<MobilePlanCardContent shortTermPlan={shortTermPlan} />} md={<PlanCardContent shortTermPlan={shortTermPlan} />} />
  </ResponsiveSelectedCard>
);

const NoLongerEligibleCard = ({ ineligibilityReason }) => (
  <ResponsiveDisabledAddCard
    icon={<ShortTermDisabilityIcon color="gray" />}
    name="Short Term Disability"
    additionalInfo={
      <>
        <Body2 componentClass="span" color="warning">
          You are no longer eligible for short term disability insurance{" "}
        </Body2>
        <SIOverlayTrigger
          placement="bottom"
          overlay={
            <Popover showBackdrop={false} id="more-reasons">
              {ineligibilityReason()}
            </Popover>
          }
        >
          <Icon icon="question-circle" />
        </SIOverlayTrigger>
      </>
    }
  />
);

const ShortTermDisabilityCompactCard = () => {
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyIDToken = companyApplication?.companyIDToken;
  const { short_term_disability: shortTermPlanId } = useSelector(getCart);
  const { data: availablePlans } = useGetShortTermDisabilityPlansQuery({ companyIDToken }, { skip: !shortTermPlanId });

  const shortTermPlan = availablePlans?.results?.find((plan) => plan.id === shortTermPlanId);

  const noLongerEligible = companyApplication?.eligibleForDisability === false;

  const employeeProfile = companyApplication?.employeeProfile;
  const fullTimeEmployees = employeeProfile?.filter((employee) => employee.fullTime);

  const ineligibilityReason = () => {
    if (fullTimeEmployees.length < 2) {
      return "Your organization needs at least two full time employees to be eligible";
    }
    if (UNSUPPORTED_DISABILITY_SIC_CODES.includes(companyApplication?.sicCode)) {
      return "Your industry is not eligible for disability";
    }
    return "You need at least two years of business history to be eligible for disability";
  };

  return noLongerEligible ? (
    <NoLongerEligibleCard ineligibilityReason={ineligibilityReason} />
  ) : shortTermPlan ? (
    <SelectedCard shortTermPlan={shortTermPlan} />
  ) : shortTermPlanId ? (
    <ResponsiveLoadingCard icon={<ShortTermDisabilityIcon />} name="Short Term Disability" />
  ) : (
    <AddPlanCard />
  );
};

export default ShortTermDisabilityCompactCard;
